<template>
  <div class="participantAudioSetting">
    <div class="content">
      <div class="outputFormat">
        <div class="header">
          <div class="tips">{{ $t('audioSettings.audioFormat') }}</div>
          <el-tooltip class="item" effect="dark" placement="top">
            <div slot="content">
              <p>{{ $t('audioSettings.audioOutput') }}</p>
              <p>{{ $t('audioSettings.selectTips') }}</p>
            </div>
            <i class="iconfont icon-help2" style="color: #909090"></i>
          </el-tooltip>
        </div>
        <el-radio-group v-model="audioMode" @change="onAudioModeChange">
          <el-radio label="mono">{{ $t('audioSettings.mono') }}</el-radio>
          <el-radio label="stereo">{{ $t('audioSettings.stereo') }}</el-radio>
        </el-radio-group>
      </div>
      <div class="audioQuality">
        <div class="tips">{{ $t('audioSettings.audioQuality') }}</div>
        <el-select
          class="qualitySelect"
          v-model="audioProfile"
          :popper-append-to-body="false"
          placeholder="Select Audio Profile"
        >
          <el-option
            v-for="profile in profiles"
            :key="profile.value"
            :label="profile.text"
            :value="profile.value"
          />
        </el-select>
      </div>
      <div class="audioSetting">
        <div class="tipsContent">
          <div class="tips">{{ $t('audioSettings.noisTips') }}</div>
          <el-tooltip class="item" effect="dark" placement="bottom">
            <div slot="content">
              <p>{{ $t('audioSettings.stereoNoOptions') }}</p>
              <p>{{ $t('audioSettings.audioOptions')}}</p>
            </div>
            <i class="iconfont icon-help2" style="color: #909090"></i>
          </el-tooltip>
        </div>
        <div class="content" v-for="(label, index) in aecOptions" :key="index">
          <el-checkbox
            v-if="audioMode === 'mono'"
            v-model="checkedOptions[label.key]"
            :disabled="audioMode === 'stereo'"
            @change="(val) => saveOperation(label.key, val)"
          >
            {{ label.text }}
          </el-checkbox>
          <el-checkbox
            v-else
            value="false"
            :disabled="true"
          >
            {{ label.text }}
          </el-checkbox>
          <span class="contentDetail">{{ label.detail }}</span>
        </div>
      </div>
    </div>
    <div class="foot">
      <el-button size="small" class="saveBtn" @click="closeAdvanced()">{{ $t('audioSettings.refreshSave') }}</el-button>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
export default {
  watch: {
    audioInfo: {
      immediate: true,
      handler (newValue) {
        this.getData(newValue)
      }
    }
  },
  props: {
    audioInfo: {
      type: Object,
      default: () => {
        return {}
      }
    },
    info: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data () {
    return {
      checkedAec: true,
      checkedAns: true,
      checkedAgc: true,
      checkedOptions: {
        checkedAec: true,
        checkedAns: true,
        checkedAgc: true
      },
      audioMode: 'mono',
      audioProfile: 'high_quality'
    }
  },
  computed: {
    ...mapState({
      State: (state) => state,
      eventInfo: state => state.eventInfo
    }),
    // 参数列表
    // https://doc.shengwang.cn/api-ref/rtc/javascript/globals#audioencoderconfigurationpreset
    profiles () {
      return this.audioMode === 'mono'
        ? [
            { text: 'Speech Low Quality (Rate 16Khz,Bitrate 24Kbps)', value: 'speech_low_quality' },
            { text: 'Speech Standard (Rate 32Khz,Bitrate 24Kbps)', value: 'speech_standard' },
            { text: 'Music Standard (Rate 48Khz,Bitrate 32Kbps)', value: 'music_standard' },
            { text: 'High Quality (Rate 48Khz,Bitrate 128Kbps)', value: 'high_quality' }
          ]
        : [
            { text: 'Standard Stereo (Rate 48Khz,Bitrate 64Kbps)', value: 'standard_stereo' },
            { text: 'High Quality Stereo (Rate 48Khz,Bitrate 192Kbps)', value: 'high_quality_stereo' }
          ];
    },
    aecOptions () {
      return [
        { key: 'checkedAec', text: this.$t('coordinationParticipant.openAEC'), detail: this.$t('coordinationParticipant.aecDetail') },
        { key: 'checkedAns', text: this.$t('coordinationParticipant.openANS'), detail: this.$t('coordinationParticipant.ansDetail') },
        { key: 'checkedAgc', text: this.$t('coordinationParticipant.openAGC'), detail: this.$t('coordinationParticipant.agcDetail') }
      ];
    }
  },
  methods: {
    async getData (audioInfo) {
      this.checkedOptions.checkedAec = audioInfo?.checkedAec
      this.checkedOptions.checkedAns = audioInfo?.checkedAns
      this.checkedOptions.checkedAgc = audioInfo?.checkedAgc
      this.audioProfile = audioInfo?.audioQualityPreset || 'high_quality'

      if (this.audioProfile === 'standard_stereo' || this.audioProfile === 'high_quality_stereo') {
        this.audioMode = 'stereo'
      } else {
        this.audioMode = 'mono'
      }
    },
    saveOperation (key, val) {
      this.checkedOptions[key] = val
    },
    onAudioModeChange (newMode) {
      this.audioMode = newMode
      // 切换音频模式时，
      // 单声道的音频质量，默认为 high_quality
      // 立体声的音频质量，默认为 standard_stereo
      if (newMode === 'stereo') {
        this.audioProfile = 'standard_stereo'
      } else {
        this.audioProfile = 'high_quality'
        const userSettings = this.info.userInfo?.eventInfo?.userSettings || []
        userSettings && userSettings.forEach(item => {
          switch (item.name) {
            case 'audioAec':
              this.checkedOptions.checkedAec = item.value === 'true'
              break
            case 'audioAns':
              this.checkedOptions.checkedAns = item.value === 'true'
              break
            case 'audioAgc':
              this.checkedOptions.checkedAgc = item.value === 'true'
              break
          }
        })
      }
    },
    changeAudioSetting (value) {
      const params = [
        {
          mark: this.info.userInfo?.rtilCode + '',
          annotation: this.info.userInfo?.partyCode,
          key: 'audioSettings',
          value,
          notify: true
        }
      ]
      this.$http.post(`${this.State.config.urlInfo.partyline}/partyline-backend/behavior/noLogin/setList`, params)
        .then((res) => {})
    },
    closeAdvanced () {
      const timestamp = Date.now()
      // 根据音频模式确定复选框值
      const getCheckedValue = (key) =>
        this.audioMode === 'mono' ? this.checkedOptions[key] : false
      const audioSettings = {
        settings: {
          checkedAec: { value: getCheckedValue('checkedAec'), timestamp },
          checkedAns: { value: getCheckedValue('checkedAns'), timestamp },
          checkedAgc: { value: getCheckedValue('checkedAgc'), timestamp }
        },
        timestamp,
        audioQualityPreset: this.audioProfile
      }
      // 1.如果不是在preview页面则通过behavior存储
      // 2.如果是在preview页面且拥有rtilCode(加入过会议)则通过behavior存储
      // 3.如果是在在preview页面且没有rtilCode(没有加入过会议)则存储在本地
      if (this.info.userInfo?.rtilCode) {
        this.changeAudioSetting(JSON.stringify(audioSettings))
      }
      this.info.show = false
      if (this.$route.name === 'coordinationParticipate') {
        window.location.reload()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.participantAudioSetting {
  .header {
    display: flex;
    gap: 6px;
    align-items: center;
    margin-bottom: 16px;
  }
 .title {
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .titleText {
      color: #fff;
      font-size: 20px;
      font-weight: 600;
    }
    .icon-Cancel {
      font-size: 12px;
      color: #dbdbdb;
    }
  }
  .tips {
    color: #fff;
    font-size: 16px;
    font-weight: 500;
  }
  .outputFormat {
    margin-bottom: 32px;
    .header {
      margin-bottom: 16px;
    }
    /deep/.el-radio {
      &.is-checked {
        .el-radio__inner {
          background: #03897B;
          border-color: #03897B;
          &::after {
            background: #000;
          }
        }
      }
      .el-radio__label {
        color: #fff;
      }
      .el-radio__inner {
        background: transparent;
        border-color: #9E9E9E;
        &::after {
          background: transparent;
        }
      }
    }
  }
  .audioQuality {
    margin-bottom: 32px;
    .qualitySelect {
      width:468px;
      margin-top: 16px;
      /deep/.el-input__inner {
        color: #fff;
        border: none;
        background: #333339;
      }
      /deep/.el-popper {
        border: 0;
        box-shadow: none;
        background: #28282e;
        .el-select-dropdown__item {
          color: #fff;
          &:hover, &.hover {
            background: rgba(0, 0, 0, .3);
          }
          &.selected {
            color: #03897B
          }
        }
      }
    }
  }
  .audioSetting {
    margin-bottom: 32px;
    .tipsContent {
      display: flex;
      gap: 6px;
      align-items: center;
      margin-bottom: 16px;
    }
    .content {
      margin-bottom: 16px;
      /deep/.el-checkbox {
        margin-right: 16px;
        .el-checkbox__inner {
          width: 16px;
          height: 16px;
          border-color: #555661;
          background: transparent;
          &::after {
            height: 8px;
            left: 5px;
            width: 4px;
            border-color: #000;
          }
        }
        &.is-checked {
          .el-checkbox__inner {
            background: #03897B;
            border-color: #03897B;
          }
        }
        &.is-disabled {
          .el-checkbox__inner {
            background: #595959;
            border-color: #595959;
          }
        }
        .el-checkbox__label {
          color: #fff;
        }
      }
      .contentDetail {
        color: #909090;
      }
    }
  }
  .foot {
    padding: 24px 0 0 24px;
    text-align: right;
    position: absolute;
    bottom: 30px;
    right: 30px;
    .saveBtn {
      color: #fff;
      background: #03897B;
      border-color: #03897B;
      &:hover {
        background: #057367;
        border-color: #057367;
      }
    }
  }
}

</style>
